import React from 'react';

/**
 * IconTile — the rounded icon "chip" that sits next to headings and in cards.
 *
 * Standardises size, radius, shadow and palette so chips stop drifting between
 * blue→indigo, blue→blue, purple→pink, amber→orange at random saturations.
 *
 * variant:
 *   solid  saturated gradient tile + white icon — for headers / marquee moments.
 *   soft   tinted background + coloured icon — for inline / in-card use (calmer).
 * tone: 'brand' | 'violet' | 'emerald' | 'amber' | 'slate'
 * size: 'sm' | 'md' | 'lg'
 */

const TONES = {
    brand: { solid: 'from-blue-500 to-teal-400 shadow-blue-500/30', soft: 'bg-blue-50 text-blue-600' },
    violet: { solid: 'from-violet-500 to-purple-600 shadow-violet-500/25', soft: 'bg-violet-50 text-violet-600' },
    emerald: { solid: 'from-emerald-500 to-teal-600 shadow-emerald-500/25', soft: 'bg-emerald-50 text-emerald-600' },
    amber: { solid: 'from-amber-400 to-orange-500 shadow-amber-500/25', soft: 'bg-amber-50 text-amber-600' },
    slate: { solid: 'from-slate-500 to-slate-600 shadow-slate-500/20', soft: 'bg-slate-100 text-slate-600' },
};

const SIZES = {
    sm: { box: 'w-9 h-9 rounded-lg', icon: 18 },
    md: { box: 'w-11 h-11 rounded-xl', icon: 22 },
    lg: { box: 'w-12 h-12 rounded-xl', icon: 26 },
};

type IconComponent = React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

interface IconTileProps {
    icon?: IconComponent;
    tone?: keyof typeof TONES;
    variant?: 'solid' | 'soft';
    size?: keyof typeof SIZES;
    className?: string;
}

const IconTile = ({ icon: Icon, tone = 'brand', variant = 'solid', size = 'md', className = '' }: IconTileProps) => {
    const t = TONES[tone] || TONES.brand;
    const s = SIZES[size] || SIZES.md;
    const surface =
        variant === 'soft'
            ? t.soft
            : `bg-gradient-to-br ${t.solid} text-white shadow-md`;
    return (
        <span className={`inline-flex flex-shrink-0 items-center justify-center ${s.box} ${surface} ${className}`}>
            {Icon && <Icon size={s.icon} strokeWidth={2.1} />}
        </span>
    );
};

export default IconTile;
